/** Background tasks that run at most once per session; add new ones here. */
export type SessionTask = "termsSync" | "publicPageStats";

// Tasks started but not yet settled in this tab, so a quick remount doesn't
// start a second copy before the first has recorded that it's done.
const inFlight = new Set<string>();

/**
 * Runs a background task at most once per browser tab session per
 * Organization. Done-ness lives in sessionStorage (like home's
 * "onboardingShown"), so it survives navigating away from and back to a page
 * but not closing the tab.
 *
 * The task resolves `true` when it's done (including "nothing needed doing")
 * and `false` when it failed; only `true` is remembered, so a failed task
 * runs again next time.
 */
export const runOncePerSession = async (
  task: SessionTask,
  orgId: string,
  run: () => Promise<boolean>
): Promise<void> => {
  const key = `oncePerSession:${task}:${orgId}`;
  if (sessionStorage.getItem(key) || inFlight.has(key)) return;

  inFlight.add(key);
  try {
    const done = await run().catch(() => false);
    if (done) sessionStorage.setItem(key, "done");
  } finally {
    inFlight.delete(key);
  }
};
